import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, CardContent, Tabs, TextLink } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import { useSecurityGroupQuery } from '../hooks/useSecurityGroupQuery';
import { useSecurityGroupMutations } from '../hooks/useSecurityGroupMutations';
import { SecurityGroupForm } from '../components/SecurityGroupForm';
import { SecurityGroupDealersPanel } from '../components/SecurityGroupDealersPanel';
import { SecurityGroupProductsPanel } from '../components/SecurityGroupProductsPanel';
import { SecurityGroupUsersPanel } from '../components/SecurityGroupUsersPanel';
import { SecurityGroupSummary } from '../components/SecurityGroupSummary';

type Tab = 'details' | 'dealers' | 'products' | 'users';

const TABS: { id: Tab; label: string }[] = [
  { id: 'details', label: 'Details' },
  { id: 'dealers', label: 'Dealers' },
  { id: 'products', label: 'Products' },
  { id: 'users', label: 'Users' },
];

type BackRef = { path: string; label: string };

export function SecurityGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const backRef = (state as { backRef?: BackRef } | null)?.backRef;
  const [activeTab, setActiveTab] = useState<Tab>('details');

  const query = useSecurityGroupQuery(id);
  const { updateMutation, deleteMutation } = useSecurityGroupMutations(id);
  const parentDealerQuery = useDealerQuery(query.data?.owner_dealer_id ?? '');

  if (!id) return <p className="text-mp-error">Invalid security group ID.</p>;
  if (query.isLoading) return <p>Loading security group...</p>;
  if (query.isError || !query.data) {
    return (
      <Alert variant="error">
        <span>Security group not found or failed to load.</span>
      </Alert>
    );
  }

  const group = query.data;

  const handleDelete = () => {
    if (!window.confirm(`Delete security group "${group.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate('/admin/security-groups'),
    });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <TextLink
          to={backRef?.path ?? '/admin/security-groups'}
          color="secondary" className="text-sm"
        >
          &larr; {backRef ? `Back to ${backRef.label}` : 'Back to Security Groups'}
        </TextLink>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">{group.name}</h1>
        <PermissionGuard permission={{ resource: 'security_group', action: 'delete' }}>
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Group'}
          </Button>
        </PermissionGuard>
      </div>

      <Tabs
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        idPrefix="security-group-detail"
        ariaLabel="Security group sections"
      >
        {activeTab === 'details' && (
          <div className="space-y-6 max-w-2xl">
            <SecurityGroupSummary
              dealersCount={group.dealers_count ?? 0}
              productsCount={group.products_count ?? 0}
              usersCount={group.users_count ?? 0}
              invoicingMode={group.invoicing_mode}
            />
            <Card>
              <CardContent>
                <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
                  <dt className="font-medium text-mp-foreground">ID</dt>
                  <dd className="font-mono text-xs">{group.id}</dd>

                  <dt className="font-medium text-mp-foreground">Name</dt>
                  <dd className="font-semibold">{group.name}</dd>

                  <dt className="font-medium text-mp-foreground">Parent Dealer</dt>
                  <dd>
                    {parentDealerQuery.isLoading && (
                      <span className="text-mp-muted text-sm">Loading...</span>
                    )}
                    {parentDealerQuery.data ? (
                      <TextLink
                        to={`/admin/dealers/${parentDealerQuery.data.id}`}
                        color="secondary" className="text-sm"
                      >
                        {parentDealerQuery.data.name}
                      </TextLink>
                    ) : (
                      !parentDealerQuery.isLoading && (
                        <span className="font-mono text-xs">{group.owner_dealer_id}</span>
                      )
                    )}
                  </dd>

                  <dt className="font-medium text-mp-foreground">Created</dt>
                  <dd>{new Date(group.created_at).toLocaleString()}</dd>

                  <dt className="font-medium text-mp-foreground">Updated</dt>
                  <dd>{new Date(group.updated_at).toLocaleString()}</dd>
                </dl>
              </CardContent>
            </Card>

            <PermissionGuard permission={{ resource: 'security_group', action: 'edit' }}>
              <div className="border-t border-mp-border pt-6">
                <h3 className="font-semibold mb-2">Edit Group</h3>
                <SecurityGroupForm
                  mode="edit"
                  initialValues={{ name: group.name, invoicing_mode: group.invoicing_mode }}
                  productsCount={group.products_count ?? 0}
                  onSubmit={(data) =>
                    updateMutation.mutate(
                      { id: id!, payload: data },
                      { onSuccess: () => query.refetch() },
                    )
                  }
                  isLoading={updateMutation.isPending}
                  submitLabel="Save Changes"
                />
              </div>
            </PermissionGuard>
          </div>
        )}
        {activeTab === 'dealers' && (
          <SecurityGroupDealersPanel
            groupId={group.id}
            ownerDealerId={group.owner_dealer_id}
            dealers={group.dealers ?? []}
          />
        )}
        {activeTab === 'products' && (
          <SecurityGroupProductsPanel
            groupId={group.id}
            ownerDealerId={group.owner_dealer_id}
            products={group.products ?? []}
          />
        )}
        {activeTab === 'users' && (
          <SecurityGroupUsersPanel
            groupId={group.id}
            userPermissions={group.user_permissions ?? []}
            userDealerPermissions={group.user_dealer_permissions ?? []}
            groupDealers={group.dealers ?? []}
          />
        )}
      </Tabs>
    </section>
  );
}
